import { type RefObject } from 'react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { CenterSpinner } from '@/components/ats';
import { useScheduleLogic } from '@/components/shared/interview-scheduler/SchedulerDetails/hooks/useScheduleLogic';
import { type EventInput, type DatesSetArg } from '@fullcalendar/core';
import moment from 'moment-timezone';
import {
  SchCalToolbar,
  SelfSchCalHeader,
  SchEventContentRenderer,
} from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/Components';
import '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/SchCalStyles/SchCal.main.scss';
import { useSelfScheduleLogic } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/InterviewSelfScheduler/useSelfScheduleLogic';
import { SchTopLeftCheckbox } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/InterviewSelfScheduler/SchTopLeftCheckbox';
import { findTimeZone } from 'timezone-support';
import { cloneDeep } from 'lodash-es';
import { createSelfAvailableSlot } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/InterviewSelfScheduler/getSelfAvailableSlots';
import { KText } from '@/ui-library/components';
import { useDateFormat } from '@/hooks';
import { useSchFindTime } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewCalendar/InterviewSelfScheduler/useSchFindTime';
import { type SchedulerDetailsTypes } from '@/components/shared/interview-scheduler/SchedulerDetails/SchedulerDetails.types';
import { useMeetingRoomScheduleLogic } from '@/components/shared/interview-scheduler/SchedulerDetails/hooks/useMeetingRoomScheduleLogic';
import { MinimumNoticePopover } from '@/components/shared/scheduling-preferences/MinimumNoticePopover';
import { schedulerDetailsUtils } from '@/components/shared/interview-scheduler/SchedulerDetails/utils/schedulerDetails.utils';

interface InterviewSelfSchedulerProps {
  calendarRef: RefObject<FullCalendar>;
  interviewDataRef: { current: SchedulerDetailsTypes.InterviewData };
  registerForUpdates: (key: string, callback: (args) => void) => () => void;
  updateSharedData: (
    data: Partial<SchedulerDetailsTypes.InterviewData>
  ) => void;
  jobId: number;
  isEdit: boolean;
  timezone?: string;
}

// Main InterviewSelfScheduler component
export const InterviewSelfScheduler: React.FC<InterviewSelfSchedulerProps> = ({
  calendarRef,
  interviewDataRef,
  registerForUpdates,
  updateSharedData,
  jobId,
  isEdit,
  timezone: initialTimezone,
}) => {
  const getMinimumNoticeTimeHours = (minimumNotice: any): number => {
    if (!minimumNotice) return 0;

    if (minimumNotice.value && minimumNotice.type) {
      return minimumNotice.type === 'days'
        ? minimumNotice.value * 24
        : minimumNotice.value;
    }

    return 0;
  };

  const { datesSet, bookedSlot, schedulerState, interviewers, meetingRooms } =
    interviewDataRef?.current ?? {};

  // Track if the user has manually modified the checkboxes since the last findTimeValue update
  const [, setHasUserModified] = useState<boolean>(false);

  const { dateFormat } = useDateFormat();

  const [timezone, setTimezone] = useState(
    datesSet?.timeZone && datesSet.timeZone !== 'local'
      ? datesSet.timeZone
      : findTimeZone(moment.tz.guess())?.name
  );

  // Get current week range and timezone
  const viewTitle: string = datesSet?.view?.title || '';

  // Normalize week range to match useScheduleLogic format (which dataForAllWeeks uses)
  // Use datesSet.start directly to ensure exact match with useScheduleLogic calculation
  const currentWeekRange: string = useMemo(() => {
    if (!datesSet?.start) {
      return viewTitle;
    }

    try {
      const weekStart = moment.tz(datesSet.start, timezone).startOf('week');
      const weekEnd = weekStart.clone().endOf('week');
      return schedulerDetailsUtils.formatWeekRange(weekStart, weekEnd);
    } catch {
      return viewTitle;
    }
  }, [datesSet?.start, timezone, viewTitle]);

  // Fetch interviewer data using the useScheduleLogic hook
  const { isLoading, interviewersData, clearCache } = useScheduleLogic({
    interviewerIds: interviewers,
    // @ts-expect-error -- pre-existing type debt (extraction move)
    interviewDataRef,
    jobId,
    currentWeekRange,
    registerForUpdates,
    // @ts-expect-error -- pre-existing type debt (extraction move)
    updateSharedData: updateSharedData,
    timeZone: timezone,
    getNextWeek: true,
  });

  const {
    isLoading: isMeetingRoomLoading,
    meetingRoomsData,
    clearCache: meetingRoomClearCache,
  } = useMeetingRoomScheduleLogic({
    meetingRoomIds: meetingRooms,
    interviewDataRef,
    jobId,
    currentWeekRange,
    registerForUpdates,
    updateSharedData,
    timeZone: timezone,
    getNextWeek: true,
  });

  useEffect(() => {
    updateSharedData({
      loadingEvents: isLoading || isMeetingRoomLoading,
      userEventsInfo: interviewersData,
      roomEventsInfo: meetingRoomsData,
    });
  }, [
    isLoading,
    isMeetingRoomLoading,
    interviewersData,
    meetingRoomsData,
    updateSharedData,
  ]);

  // Get events and calendar data using useSelfScheduleLogic
  // Only generate slots for selected days (controlled by checkboxes or findTimeValue)
  const {
    events,
    hasAllDayEvent,
    fullDayEventDays,
    userAvailability,
    currentWeekState,
  } = useSelfScheduleLogic({
    dataForAllWeeks: interviewersData,
    meetingDataForAllWeeks: meetingRoomsData,
    // @ts-expect-error -- pre-existing type debt (extraction move)
    interviewDataRef,
    calendarRef,
    isLoading: isLoading || isMeetingRoomLoading,
    currentWeekRange,
    registerForUpdates,
    updateSharedData,
    isEdit,
  });

  // Compute available days (not past, no full-day events, and with generated slots)
  const [availableDays, unavailableDays] = useMemo(() => {
    // Note: We rely on generated/custom slots presence for availability instead of raw weekly availability

    let startOfWeek = moment
      .tz(datesSet?.start, timezone)
      .startOf('week')
      .day(0); // Force Sunday as first day

    // Adjust startOfWeek if it's a Saturday
    if (startOfWeek.day() === 6) {
      startOfWeek = startOfWeek.add(1, 'day');
    }

    const now = moment().tz(timezone);

    const potentialDays = [];
    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.clone().add(i, 'days');
      const dateKey = day.format(dateFormat?.label);
      if (!day.isBefore(now, 'day') && !fullDayEventDays[dateKey]) {
        potentialDays.push(dateKey);
      }
    }

    const availableDaysWithSlots = [];
    const unavailableDaysDueToNoSlots = [];

    potentialDays.forEach((dateKey) => {
      const dayState = currentWeekState.days[dateKey];
      const hasGeneratedSlots = (dayState?.slotsGenerated?.length || 0) > 0;
      const hasCustomSlots = (dayState?.customSlots?.length || 0) > 0;

      if (hasGeneratedSlots || hasCustomSlots) {
        availableDaysWithSlots.push(dateKey);
      } else {
        unavailableDaysDueToNoSlots.push(dateKey);
      }
    });

    return [availableDaysWithSlots, unavailableDaysDueToNoSlots];
  }, [
    datesSet?.start,
    timezone,
    dateFormat?.label,
    fullDayEventDays,
    currentWeekState.days,
  ]);

  // Filter events to exclude removed slots
  const filteredEvents: EventInput[] = useMemo(() => {
    const out = events.filter((event: EventInput) => {
      if (event.category !== 'available') return true;
      const dateKey: string = moment(event.start)
        .tz(timezone)
        .format(dateFormat?.label);
      const dayState = currentWeekState.days[dateKey] || {
        isSelected: false,
        slotsGenerated: [],
        removedSlots: [],
        customSlots: [],
      };
      return !dayState.removedSlots.includes(event.id);
    });
    return out;
  }, [events, timezone, dateFormat?.label, currentWeekState.days]);

  // Compute the state of the "All" checkbox
  const { isAllChecked, isIndeterminate, isDisabled } = useMemo(() => {
    const selectedAvailableDays = Object.entries(
      currentWeekState.days || {}
    ).filter(
      ([dateKey, dayState]) =>
        availableDays.includes(dateKey) && dayState.isSelected
    ).length;

    const totalAvailableDays = availableDays.length;

    return {
      isDisabled: totalAvailableDays === 0,
      isAllChecked:
        totalAvailableDays > 0 && selectedAvailableDays === totalAvailableDays,
      isIndeterminate:
        selectedAvailableDays > 0 && selectedAvailableDays < totalAvailableDays,
    };
  }, [currentWeekState, availableDays]);

  // Clean up cache on unmount
  useEffect(() => {
    return () => {
      clearCache();
      meetingRoomClearCache();
    };
  }, [clearCache, meetingRoomClearCache]);

  const [lastResetAllSlots, setLastResetAllSlots] = useState<
    boolean | undefined
  >(undefined);

  const { setLastFindTimeValue } = useSchFindTime({
    interviewDataRef,
    registerForUpdates,
    updateSharedData,
    schedulerState: interviewDataRef.current.schedulerState,
    calendarRef,
    timezone,
    datesSet: interviewDataRef.current.datesSet,
    fullDayEventDays,
    currentWeekRange,
    availableDays,
    userAvailability,
    dateFormatLabel: dateFormat?.label,
    setHasUserModified,
  });

  useEffect(() => {
    const unregisterReset = registerForUpdates('resetAllSlots', () => {
      const reset: boolean | undefined = interviewDataRef.current.resetAllSlots;

      // SCENARIO 1: Skip if resetAllSlots hasn't changed
      // Business Logic: Prevents redundant processing when the reset state is already applied
      // the clear button itself will not be enabled - so hiding this in ui
      // if (reset === lastResetAllSlots) {
      //   // resetAllSlots unchanged, skipping
      //   return;
      // }

      // SCENARIO 2: Handle resetAllSlots becoming false
      // Use Case: External component clears the reset flag; no action needed beyond updating local state
      if (!reset) {
        // resetAllSlots is false, no action needed
        setLastResetAllSlots(false);
        return;
      }

      // SCENARIO 3: Handle resetAllSlots becoming true
      // Use Case: User or external action requests a full reset of all selections and slots across all weeks
      // Business Logic: Clears all selections and all types of slots (customSlots, removedSlots, slotsGenerated) for every week
      const updatedSchedulerState = cloneDeep(schedulerState);

      Object.keys(updatedSchedulerState).forEach((weekRange) => {
        const weekState = updatedSchedulerState[weekRange];
        const resetDays: typeof weekState.days = {};
        Object.keys(weekState.days).forEach((dateKey) => {
          resetDays[dateKey] = {
            ...weekState.days[dateKey],
            isSelected: false,
            customSlots: [], // Clear custom slots
            removedSlots: [], // Clear removed slots
            slotsGenerated: [], // Clear generated slots
          };
        });
        updatedSchedulerState[weekRange] = {
          ...weekState,
          isAllChecked: false,
          days: resetDays,
        };
      });

      setHasUserModified(false);
      setLastFindTimeValue('');
      setLastResetAllSlots(true);

      const resetPayload = {
        schedulerState: updatedSchedulerState,
        findTimeValue: '',
        resetAllSlots: false, // Reset the flag to prevent re-trigger
      };

      // Ensure this update takes precedence by directly updating interviewDataRef
      interviewDataRef.current.schedulerState = updatedSchedulerState;
      interviewDataRef.current.findTimeValue = '';
      interviewDataRef.current.resetAllSlots = false;
      updateSharedData(resetPayload);
    });

    return () => {
      unregisterReset();
    };
  }, [
    registerForUpdates,
    schedulerState,
    lastResetAllSlots,
    interviewDataRef,
    updateSharedData,
    setHasUserModified,
    setLastFindTimeValue,
  ]);

  // Clear customSlots and removedSlots when meeting rooms change (preserves isSelected)
  useEffect(() => {
    const unregisterClearCustomSlots = registerForUpdates(
      'clearCustomSlots',
      () => {
        const shouldClear: boolean | undefined =
          interviewDataRef.current.clearCustomSlots;
        if (!shouldClear) return;

        const updatedSchedulerState = cloneDeep(schedulerState);

        Object.keys(updatedSchedulerState).forEach((weekRange) => {
          const weekState = updatedSchedulerState[weekRange];
          const resetDays: typeof weekState.days = {};
          Object.keys(weekState.days).forEach((dateKey) => {
            resetDays[dateKey] = {
              ...weekState.days[dateKey],
              customSlots: [],
              removedSlots: [],
              slotsGenerated: [],
            };
          });
          updatedSchedulerState[weekRange] = { ...weekState, days: resetDays };
        });

        interviewDataRef.current.schedulerState = updatedSchedulerState;
        interviewDataRef.current.clearCustomSlots = false;
        updateSharedData({
          schedulerState: updatedSchedulerState,
          clearCustomSlots: false,
        });
      }
    );

    return () => {
      unregisterClearCustomSlots();
    };
  }, [registerForUpdates, schedulerState, interviewDataRef, updateSharedData]);

  // Get event handlers for calendar interactions
  const handleSelect = (info) => {
    const { startStr, onTopOfEvent } = info;

    const eventDate = moment.tz(startStr, timezone).format(dateFormat?.label);
    const eventDayCustomSlots = currentWeekState.days[eventDate]?.customSlots;
    const calendarStrToSameTimezone = moment(startStr)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

    // SCENARIO 1: Prevent duplicate custom slots
    // Business Logic: Ensures the same slot isn't added twice on the same day
    const isDuplicate = eventDayCustomSlots?.some(
      (slot) =>
        slot.start === startStr || slot.start === calendarStrToSameTimezone
    );

    if (isDuplicate) return;

    const startMoment = moment.tz(startStr, timezone);
    const endMoment = startMoment.clone().add(bookedSlot?.duration, 'minutes');

    const dateKey = startMoment.format(dateFormat?.label);
    const id = 'custom' + dateKey;
    const duration = bookedSlot?.duration;
    const isCustomSlot = onTopOfEvent === true;
    const newSlot = createSelfAvailableSlot(
      id,
      startMoment,
      endMoment,
      duration,
      isCustomSlot
    );

    const dayState = currentWeekState.days[dateKey] || {
      isSelected: false,
      slotsGenerated: [],
      customSlots: [],
      removedSlots: [],
    };

    // SCENARIO 2: Add a custom slot to the selected day
    // Use Case: User manually adds a slot by clicking on the calendar
    // Business Logic: Marks the day as selected and adds the slot without affecting "All" selection
    const updatedDays = {
      ...currentWeekState.days,
      [dateKey]: {
        ...dayState,
        // isSelected: false, // Automatically don't select the day when adding a custom slot (confirmed behavior)
        customSlots: [...dayState.customSlots, newSlot],
      },
    };

    // SCENARIO 3: Update shared state without resetting findTimeValue
    // Business Logic: Preserves existing "All" selection and other days' states
    setHasUserModified(true);
    updateSharedData({
      schedulerState: {
        ...schedulerState,
        [currentWeekRange]: {
          ...currentWeekState,
          days: updatedDays,
          // isAllChecked remains unchanged to maintain "All" state
        },
      },
      // findTimeValue omitted to avoid triggering reset in useEffect
    });
  };

  // Function to remove a slot by ID
  const removeSlot = (slotId: string): void => {
    const slotToRemove: EventInput | undefined = events.find(
      (event: EventInput) => event.id === slotId
    );
    if (!slotToRemove) return;

    const dateKey: string = moment(slotToRemove.start)
      .tz(timezone)
      .format(dateFormat?.label);
    const dayState = currentWeekState.days[dateKey] || {
      isSelected: false,
      slotsGenerated: [],
      customSlots: [],
      removedSlots: [],
    };

    const updatedDays: {
      [dateKey: string]: {
        isSelected: boolean;
        slotsGenerated: SchedulerDetailsTypes.AvailableSlot[];
        customSlots: SchedulerDetailsTypes.AvailableSlot[];
        removedSlots: string[];
      };
    } = { ...currentWeekState.days };

    // Check if the slot is from customSlots
    const isCustomSlot = dayState.customSlots.some(
      (slot) => slot.id === slotId
    );

    if (isCustomSlot) {
      // Remove directly from customSlots
      const updatedCustomSlots = dayState.customSlots.filter(
        (slot) => slot.id !== slotId
      );
      updatedDays[dateKey] = {
        ...dayState,
        customSlots: updatedCustomSlots,
        // No change to removedSlots or isSelected
      };
    } else {
      // Handle removal from slotsGenerated
      const newRemovedSlots = [...dayState.removedSlots, slotId];
      const remainingGeneratedSlots = dayState.slotsGenerated.filter(
        (slot: SchedulerDetailsTypes.AvailableSlot) =>
          !newRemovedSlots.includes(slot.id)
      ).length;

      updatedDays[dateKey] = {
        ...dayState,
        removedSlots: newRemovedSlots,
        isSelected: remainingGeneratedSlots > 0, // Deselect only if no generated slots remain
      };
    }

    // Mark that the user has manually modified the state
    setHasUserModified(true);

    updateSharedData({
      schedulerState: {
        ...schedulerState,
        [currentWeekRange]: {
          ...currentWeekState,
          days: updatedDays,
          isAllChecked: false, // Reset isAllChecked since we're modifying slots
        },
      },
      findTimeValue: '', // Clear findTimeValue when manually removing slots
    });
  };

  // Handle "All" checkbox toggle
  const handleAllCheckboxChange = (): void => {
    if (!currentWeekRange) {
      return;
    }

    const newIsAllChecked = !isAllChecked;
    const updatedDays: typeof currentWeekState.days = cloneDeep(
      currentWeekState.days
    );

    // SCENARIO 1: Handle "Select All" checkbox toggle
    // Use Case: User checks/unchecks the "Select All" checkbox for the current week
    // Business Logic: When unchecked, clear all selections in the current week; when checked, select all available days in the current week
    Object.keys(updatedDays).forEach((dateKey) => {
      const isDayAvailable = availableDays.includes(dateKey);
      updatedDays[dateKey] = {
        ...updatedDays[dateKey],
        isSelected: newIsAllChecked ? isDayAvailable : false, // Clear selections when unchecked
        customSlots: newIsAllChecked ? updatedDays[dateKey].customSlots : [], // Optionally clear custom slots when unchecked
        removedSlots: [],
      };
    });

    setHasUserModified(true);
    // setLastFindTimeValue(newIsAllChecked ? 'this_week' : '');

    const res = {
      schedulerState: {
        ...schedulerState,
        [currentWeekRange]: {
          ...currentWeekState,
          isAllChecked: newIsAllChecked,
          days: updatedDays,
        },
      },
      // findTimeValue: newIsAllChecked ? 'this_week' : '',
    };

    updateSharedData(res);
  };

  const [warningMessage, setWarningMessage] = useState(null);

  // Clear message after 3 seconds
  useEffect(() => {
    if (warningMessage) {
      const timer = setTimeout(() => setWarningMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [warningMessage]);

  const getScrollTime = (timezone) => {
    let targetTime;

    const now = moment().tz(timezone);
    const startOfDay = now.clone().startOf('day');
    const endOfDay = startOfDay.clone().add(20, 'hours');
    const startOfDayPlusTwo = startOfDay.clone().add(2, 'hours');

    if (now.isBefore(endOfDay) && now.isAfter(startOfDayPlusTwo)) {
      targetTime = now.clone().subtract(2, 'hours');
    } else {
      targetTime = now.clone();
    }

    return targetTime.format('HH:mm:ss');
  };

  // Handle event drop (drag-and-drop of a slot)
  const handleEventDrop = (info) => {
    const { event, oldEvent, revert } = info;
    const oldStart = moment.tz(oldEvent.startStr, timezone);
    const oldEnd = moment.tz(oldEvent.endStr, timezone);
    const newStart = moment.tz(event.startStr, timezone);
    const newEnd = moment.tz(event.endStr, timezone);
    const newDateKey = newStart.format(dateFormat?.label);
    const oldDateKey = moment
      .tz(oldEvent.start, timezone)
      .format(dateFormat?.label);

    const oldSlotId = `${oldDateKey}-${oldStart.format(
      'HH:mm'
    )}-${oldEnd.format('HH:mm')}`;
    const currentSlotId = `${newDateKey}-${newStart.format(
      'HH:mm'
    )}-${newEnd.format('HH:mm')}`;
    const currentCustomSlotId = `custom${currentSlotId}`;
    const oldCustomSlotId = `custom${oldSlotId}`;

    // Step 1: Get old and new day states
    const oldDayState = currentWeekState.days[oldDateKey] || {
      isSelected: false,
      slotsGenerated: [],
      customSlots: [],
      removedSlots: [],
    };
    const newDayState = currentWeekState.days[newDateKey] || {
      isSelected: false,
      slotsGenerated: [],
      customSlots: [],
      removedSlots: [],
    };

    // Step 2: Validate against overlaps
    const hasCustomOverlap = newDayState.customSlots.some((slot) => {
      const existingStart = moment.tz(slot.start, timezone);
      const existingEnd = moment.tz(slot.end, timezone);
      return (
        slot.id !== oldCustomSlotId &&
        existingStart.isSame(newStart) &&
        existingEnd.isSame(newEnd)
      );
    });

    const hasGeneratedOverlap = newDayState.isSelected
      ? newDayState.slotsGenerated
          .filter((slot) => !newDayState.removedSlots.includes(slot.id))
          .some((slot) => {
            const existingStart = moment.tz(slot.start, timezone);
            const existingEnd = moment.tz(slot.end, timezone);
            return existingStart.isSame(newStart) && existingEnd.isSame(newEnd);
          })
      : false;

    if (hasCustomOverlap || hasGeneratedOverlap) {
      revert();
      return;
    }

    // Step 3: Create the new custom slot
    const newSlot = createSelfAvailableSlot(
      newDateKey,
      newStart,
      newEnd,
      bookedSlot?.duration,
      true //  This is a user-created custom slot, use success styling
    );
    newSlot.id = currentCustomSlotId;

    // Step 4: Handle valid drop
    const updatedDays = { ...currentWeekState.days };

    // Check if the old slot was a custom slot or generated slot
    const isCustomSlot = oldDayState.customSlots.some(
      (slot) => slot.id === oldCustomSlotId
    );
    const isGeneratedSlot = oldDayState.slotsGenerated.some(
      (slot) => slot.id === oldSlotId
    );

    if (oldDateKey === newDateKey) {
      // Same-day drop
      const dayState = oldDayState; // Same as newDayState since oldDateKey === newDateKey
      let updatedCustomSlots = [...dayState.customSlots];
      const updatedRemovedSlots = [...dayState.removedSlots];

      if (isCustomSlot) {
        // Update existing custom slot
        updatedCustomSlots = updatedCustomSlots.map((slot) =>
          slot.id === oldCustomSlotId ? newSlot : slot
        );
      } else if (isGeneratedSlot) {
        // Convert generated slot to custom slot
        updatedCustomSlots = [...updatedCustomSlots, newSlot];
        if (!updatedRemovedSlots.includes(oldSlotId)) {
          updatedRemovedSlots.push(oldSlotId); // Mark original generated slot as removed
        }
      } else {
        // Slot not found in either, assume it's new (shouldn't happen if event exists)
        updatedCustomSlots.push(newSlot);
      }

      updatedDays[oldDateKey] = {
        ...dayState,
        customSlots: updatedCustomSlots,
        removedSlots: updatedRemovedSlots,
      };
    } else {
      // Different-day drop
      // Remove from old day
      const updatedOldCustomSlots = oldDayState.customSlots.filter(
        (slot) => slot.id !== oldCustomSlotId
      );
      const updatedOldRemovedSlots =
        isGeneratedSlot && !oldDayState.removedSlots.includes(oldSlotId)
          ? [...oldDayState.removedSlots, oldSlotId]
          : oldDayState.removedSlots;

      updatedDays[oldDateKey] = {
        ...oldDayState,
        customSlots: updatedOldCustomSlots,
        removedSlots: updatedOldRemovedSlots,
      };

      // Add to new day
      const updatedNewCustomSlots = [...newDayState.customSlots, newSlot];
      updatedDays[newDateKey] = {
        ...newDayState,
        customSlots: updatedNewCustomSlots,
        removedSlots: newDayState.removedSlots,
      };
    }

    // Step 5: Update the shared state
    setHasUserModified(true);
    try {
      const newValues = {
        schedulerState: {
          ...schedulerState,
          [currentWeekRange]: {
            ...currentWeekState,
            days: updatedDays,
            isAllChecked: false,
          },
        },
        findTimeValue: '',
      };
      updateSharedData(newValues);
    } catch (error) {
      console.error('handleEventDrop: Error in updateSharedData:', error);
      revert();
    }
  };

  // Local loader for edit mode, only on first mount
  const [localLoader, setLocalLoader] = useState(false);
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isEdit) {
      setLocalLoader(true);
      timer = setTimeout(() => {
        setLocalLoader(false);
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isEdit]);
  return (
    <Flex flexDirection="column" h="100%" position="relative">
      {/* Loading overlay */}
      {(isLoading || isMeetingRoomLoading || localLoader) && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          bg="rgba(248, 249, 252, 0.4)"
          zIndex="10"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="not-allowed"
        >
          <CenterSpinner
            spinnerProps={{ color: '#797DFF', size: 'xl', speed: '1s' }}
          />
        </Box>
      )}

      {/* Calendar toolbar */}
      <SchCalToolbar
        calendarRef={calendarRef}
        registerForUpdates={registerForUpdates}
        setTimezone={setTimezone}
        initialTimezone={initialTimezone}
      />

      {/* "ALL" checkbox */}
      <SchTopLeftCheckbox
        checked={isAllChecked}
        isIndeterminate={isIndeterminate}
        onChange={handleAllCheckboxChange}
        isDisabled={isDisabled}
        currentWeekRange={currentWeekRange}
        interviewDataRef={interviewDataRef}
        timezone={timezone}
      />

      {/* FullCalendar component */}
      <Box
        h="100%"
        w="100%"
        overflow="hidden"
        className="k-interview-scheduler-calendar k-interview-self-scheduler"
      >
        {(() => {
          const fullCalendar = (
            <FullCalendar
              key={timezone}
              ref={calendarRef}
              plugins={[
                timeGridPlugin,
                interactionPlugin,
                momentTimezonePlugin,
              ]}
              nowIndicator={true}
              initialView="timeGridWeek"
              initialDate={new Date()}
              timeZone={timezone}
              dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
              dayHeaderContent={(arg) => {
                return (
                  <SelfSchCalHeader
                    {...arg}
                    fullDayEventDays={fullDayEventDays}
                    currentWeekUserAvailability={
                      userAvailability[currentWeekRange]
                    }
                    currentWeekRange={currentWeekRange}
                    // @ts-expect-error -- pre-existing type debt (extraction move)
                    updateSharedData={updateSharedData}
                    setHasUserModified={setHasUserModified}
                    registerForUpdates={registerForUpdates}
                    interviewDataRef={interviewDataRef}
                    timezone={timezone}
                    currentWeekState={currentWeekState}
                    unavailableDays={unavailableDays}
                    // @ts-expect-error -- pre-existing type debt (extraction move)
                    dataForAllWeeks={interviewersData}
                    checkboxProps={{
                      isSelfSchedule: true,
                      onCheckboxChange: handleAllCheckboxChange,
                    }}
                  />
                );
              }}
              eventContent={(info) => (
                <SchEventContentRenderer
                  eventInfo={info}
                  registerForUpdates={registerForUpdates}
                  interviewDataRef={interviewDataRef}
                  updateSharedData={updateSharedData}
                  removeSlot={removeSlot}
                  onSelect={handleSelect}
                  timezone={timezone}
                />
              )}
              eventOrderStrict={true}
              eventOrder="-extendedProps.priority,start_time"
              headerToolbar={false}
              selectable={true}
              selectMirror={false}
              dayMaxEvents
              weekends
              height="100%"
              allDaySlot={hasAllDayEvent}
              allDayContent={() => <KText variant="small_text">All day</KText>}
              slotDuration="00:15:00"
              slotLabelInterval={{ hours: 1 }}
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              scrollTime={getScrollTime(timezone)}
              eventDrop={handleEventDrop}
              selectAllow={(selectInfo) => {
                const duration = moment
                  .tz(selectInfo.endStr, timezone)
                  .diff(moment.tz(selectInfo.startStr, timezone), 'minutes');

                const now = moment().tz(timezone);
                const minimumNoticeEnabled =
                  interviewDataRef.current.minimum_notice_enabled;
                const minimumNoticeEndTime = minimumNoticeEnabled
                  ? now
                      .clone()
                      .add(
                        getMinimumNoticeTimeHours(
                          interviewDataRef.current.minimum_notice_config
                        ),
                        'hours'
                      )
                  : now.clone();
                const isAllowed =
                  selectInfo.start >= minimumNoticeEndTime.toDate() &&
                  duration <= bookedSlot?.duration;

                if (!isAllowed) {
                  // setWarningMessage(
                  //   'Cannot add slots in the past. Please add slots in future.'
                  // );
                }
                return isAllowed;
              }}
              editable={false} // Disables dragging and resizing for all events
              expandRows={true}
              eventAllow={(dropInfo) => {
                const now = moment().tz(timezone);
                const minimumNoticeEnabled =
                  interviewDataRef.current.minimum_notice_enabled;
                const minimumNoticeEndTime = minimumNoticeEnabled
                  ? now
                      .clone()
                      .add(
                        getMinimumNoticeTimeHours(
                          interviewDataRef.current.minimum_notice_config
                        ),
                        'hours'
                      )
                  : now.clone();
                const isAllowed =
                  dropInfo.start >= minimumNoticeEndTime.toDate();
                return isAllowed;
              }}
              stickyHeaderDates={true}
              // slotEventOverlap={false}
              select={handleSelect}
              slotLabelContent={({ text }) => {
                return (
                  <KText variant="small_text" w="47px" alignItems="center">
                    {text}
                  </KText>
                );
              }}
              slotEventOverlap={true}
              slotLabelFormat={{
                hour: 'numeric',
                minute: '2-digit',
                omitZeroMinute: true,
                meridiem: true,
                separator: ' - ',
              }}
              datesSet={(arg: DatesSetArg) => {
                updateSharedData({
                  // @ts-expect-error -- pre-existing type debt (extraction move)
                  datesSet: arg,
                  // @ts-expect-error -- pre-existing type debt (extraction move)
                  currentWeek: arg?.view?.getCurrentData?.()?.viewTitle,
                });
              }}
              events={localLoader ? [] : filteredEvents}
            />
          );

          if (interviewDataRef.current.minimum_notice_enabled) {
            return (
              <MinimumNoticePopover
                minimumNoticeConfig={
                  interviewDataRef.current.minimum_notice_config
                }
              >
                {fullCalendar}
              </MinimumNoticePopover>
            );
          } else {
            return fullCalendar;
          }
        })()}
      </Box>
      {warningMessage && (
        <Box
          position="absolute"
          top="10px"
          left="50%"
          transform="translateX(-50%)"
          bg="yellow.100"
          color="yellow.800"
          p="2"
          borderRadius="md"
          boxShadow="md"
          zIndex="20"
          textAlign="center"
        >
          {warningMessage}
        </Box>
      )}
    </Flex>
  );
};
